import React, { useRef, useState, useCallback, useEffect } from 'react';
import {
  AskRequest,
  AskRequestPriorities,
  HoneAbilityActions,
  HoneAbilitySubjects,
  QueueView,
} from '@hone-automation/common';
import classNames from 'classnames';
import { prefixClass } from 'lib/utils';
import {
  IconChevron,
  IconQuestion,
  IconPromote,
  IconMoreV,
  IconReopen,
  IconValidateNew,
  IconAssignmentNew,
  IconKaiSynth,
  IconFolder,
  IconFolderDelete,
} from 'components/Icons';
import RequestType from '../../Common/RequestType/RequestType';
import { MobileSlideUpPanel } from '../MobileSlideUpPanel';
import SummarizePanel from 'presentation/components/Queue/panels/SummarizePanel/SummarizePanel';
import { useAuthContext } from 'context/useAuthContext';
import { MessageList, MessageListRef } from '../../Panels/ConversationPanel/components/MessageList';
import { AssignmentButton } from '../../Panels/ConversationPanel/components/Header/components/AssignmentButton';
import { MobileActionsPanel } from '../MobileActionsPanel';
import { DueDateBadge } from '../../Common/DueDateBadge';
import IconButton from '../../Common/IconButton';
import CMComposer, { CMComposerRef } from 'presentation/components/Queue/CMComposer/CMComposer';
import { CMComposerContexts } from 'presentation/components/Queue/CMComposer/types';
import { PendingMessageData } from '../../Panels/ConversationPanel/components/MessageList/PendingMessage';
import { AskRequestQueueComment } from '../../Cards/MessagesCard/messagesCardFactory';
import { usePatchQueueRequestMutation } from 'hooks/queue/';
import { useQueueContext } from 'presentation/components/Queue/QueueContext';
import { useParams } from 'react-router-dom';
import { parseLocalDate } from '../../Panels/BrowserPanel/utils/dateGrouping';
import { getShortId } from '../../utils/shortId';
import { getRequestActionVisibility } from '../../utils/actionVisibility';
import { ReopenDialog } from '../../Panels/ConversationPanel/components/Header/components/ActionsDropdown/ReopenDialog';
import { EmptyState } from '../../Common/EmptyState';
import { MobileEditableTitle } from '../MobileEditableTitle/MobileEditableTitle';
import { MobileGroupPickerPanel } from '../MobileGroupPickerPanel';
import { useAddToGroup, useRemoveFromGroup, useDissolveGroup, useCreateGroup } from 'hooks/queue';
import { useGroups } from '../../../Dashboard/useGroups';
import './MobileDetailsScreen.scss';

interface MobileDetailsScreenProps {
  /** The request to display */
  request: AskRequest;
  /** Comments for the request (optional, falls back to request.comments) */
  comments?: AskRequestQueueComment[];
  /** Callback when back button is pressed */
  onBack: () => void;
}

/**
 * MobileDetailsScreen - Full conversation view for mobile
 *
 * Structure:
 * - Header: Back button + title + more button
 * - MessageList: Scrollable conversation (fills available space)
 * - CMComposer: Message input at bottom
 *
 * @see Desktop equivalent: ConversationPanel
 * @see RN equivalent: QueueDetailScreen
 */
export const MobileDetailsScreen: React.FC<MobileDetailsScreenProps> = ({
  request: requestProp,
  comments: commentsProp,
  onBack,
}) => {
  const prefix = prefixClass('ksq2-mobile-details');
  const composerRef = useRef<CMComposerRef>(null);
  const messageListRef = useRef<MessageListRef>(null);
  const [pendingMessage, setPendingMessage] = useState<PendingMessageData | null>(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [isReopenOpen, setIsReopenOpen] = useState(false);
  const [isSummarizeOpen, setIsSummarizeOpen] = useState(false);

  // Queue context for getting live updates and updating requests
  const { dispatch, state } = useQueueContext();
  const { requests, selectedItem, activeView, meId } = state;

  // Use selectedItem from context for live updates, fall back to prop
  // This ensures updates from AssignmentButton, MobileActionsDropdown, etc. are reflected
  const request = (selectedItem?.id === requestProp.id ? selectedItem : requestProp) as AskRequest;

  // Use comments from props or fall back to request.comments
  const comments = (commentsProp || request.comments || []) as AskRequestQueueComment[];

  const { currentLocationAbility } = useAuthContext();
  const canManageQueue = currentLocationAbility?.can?.(HoneAbilityActions.manage, HoneAbilitySubjects.Queue) ?? false;
  const { locationId } = useParams<{ locationId: string }>();

  // Group support
  const groups = useGroups();
  const isGroupContainer = request.type === 'Group';
  const hasParentGroup = !!request.parentId;
  const hasOtherGroups = hasParentGroup
    ? groups.filter((g: { id: string }) => g.id !== request.parentId).length > 0
    : false;
  const [isGroupPickerOpen, setIsGroupPickerOpen] = useState(false);
  const [isGroupPickerMove, setIsGroupPickerMove] = useState(false);
  // Track which request ID was force-moved (by priority mutation).
  // Using an ID instead of a boolean ensures it resets automatically when navigating
  // to a different request, even if the component isn't remounted (300ms close animation).
  const [forcedMovedId, setForcedMovedId] = useState<string | null>(null);
  const forcedMoved = forcedMovedId === request.id;

  const { addToGroup } = useAddToGroup(locationId || '');
  const { removeFromGroup } = useRemoveFromGroup(locationId || '');
  const { dissolveGroup } = useDissolveGroup(locationId || '');
  const { createGroup } = useCreateGroup(locationId || '');

  const handleCreateGroupWithAskRequests = useCallback(async () => {
    const title = prompt('Enter group name:');
    if (!title?.trim()) return;
    await createGroup({ title: title.trim(), childRequestIds: [request.id] });
  }, [createGroup, request.id]);

  const handleAddToGroup = useCallback(
    (groupId: string) => {
      addToGroup(request.id, groupId);
    },
    [addToGroup, request.id]
  );

  const handleRemoveFromGroup = useCallback(() => {
    if (!request.parentId) return;
    removeFromGroup(request.id);
  }, [removeFromGroup, request.id, request.parentId]);

  const handleDissolveGroup = useCallback(async () => {
    const count = groups.find(g => g.id === request.id)?.requestCount ?? 0;
    const dissolved = await dissolveGroup(request.id, request.type || '', count);
    if (dissolved) {
      dispatch({ type: 'SET_SELECTED_ITEM', payload: null });
      onBack();
    }
  }, [dissolveGroup, request.id, request.type, groups, dispatch, onBack]);

  const handleGroupPickerSelect = useCallback(
    (groupId: string) => {
      handleAddToGroup(groupId);
      setIsGroupPickerOpen(false);
    },
    [handleAddToGroup]
  );

  const parentGroupName = hasParentGroup
    ? groups.find((g: { id: string }) => g.id === request.parentId)?.name
    : undefined;

  const handleGoToParentGroup = useCallback(() => {
    if (!request.parentId) return;
    dispatch({ type: 'SET_ACTIVE_GROUP_ID', payload: request.parentId });
    dispatch({ type: 'SET_ACTIVE_VIEW', payload: 'Groups' });
    onBack();
  }, [request.parentId, dispatch, onBack]);

  const cannotReopen = request.status === 'Closed' && !request.canReopen;
  const isBlocker = request.priority === 'Blocker';
  const { showBlockerToggle, showReopen } = getRequestActionVisibility(request, { canManageQueue });

  const requestBelongsToView = useCallback(
    (req: AskRequest) => {
      if (!activeView) return true;
      if (activeView === 'Blockers') return req.priority === 'Blocker' || req.type === 'Group';
      // Group containers are not view-specific
      if (req.type === 'Group') return true;

      switch (activeView) {
        case 'Me':
          return req.status === 'Open' && req.assignee === meId;
        case 'My Team':
          return req.status === 'Open' && req.assignee !== meId;
        case 'Client':
          return req.status === 'In Progress';
        case 'Completed':
          return req.status === 'Closed';
        case 'Groups':
          return true;
        default:
          return true;
      }
    },
    [activeView, meId]
  );

  const getTargetView = useCallback(
    (req: AskRequest) => {
      if (req.priority === 'Blocker') return 'Blockers';
      if (req.status === 'Closed') return 'Completed';
      if (req.status === 'In Progress') return 'Client';
      if (req.status === 'Open') return req.assignee === meId ? 'Me' : 'My Team';
      return undefined;
    },
    [meId]
  );

  const movedOutOfView = forcedMoved || (activeView ? !requestBelongsToView(request) : false);
  const targetView = getTargetView(request);
  const viewLabels: Record<string, string> = {
    Blockers: 'Blockers',
    Me: 'Me',
    'My Team': 'My Team',
    Client: 'Client',
    Completed: 'Resolved',
  };
  const targetLabel = targetView ? viewLabels[targetView] : 'another view';

  const handleGoToTargetView = useCallback(() => {
    if (targetView) {
      dispatch({ type: 'SET_ACTIVE_VIEW', payload: targetView as QueueView });
    }
    onBack();
  }, [dispatch, onBack, targetView]);

  const movedIcon =
    targetView === 'Completed' ? (
      <IconValidateNew />
    ) : targetView === 'Blockers' ? (
      <IconPromote />
    ) : activeView === 'Completed' ? (
      <IconReopen />
    ) : (
      <IconAssignmentNew />
    );

  // Parse due date for display
  const dueDate = request.dueDate ? parseLocalDate(request.dueDate) : null;
  const isDueDatePastDue = dueDate ? dueDate < new Date() : false;

  // Priority toggle mutation
  const priorityMutation = usePatchQueueRequestMutation(data => {
    if (data) {
      dispatch({
        type: 'SET_REQUESTS',
        payload: requests.map(req => (req.id === data.id ? data : req)),
      });
      dispatch({ type: 'SET_SELECTED_ITEM', payload: data });
      // Blocking a request from a non-Blockers view means it "moved" to Blockers.
      // requestBelongsToView returns true for open blockers in all views (to avoid false
      // positives on initial click), so we force the moved screen here explicitly.
      if (data.priority === 'Blocker' && activeView !== 'Blockers') {
        setForcedMovedId(data.id);
      }
    }
  });

  const handleTogglePriority = useCallback(() => {
    const nextPriority = isBlocker ? AskRequestPriorities.Normal : AskRequestPriorities.Blocker;
    priorityMutation.mutate({ id: request.id, priority: nextPriority });
  }, [isBlocker, priorityMutation, request.id]);

  const reopenMutation = usePatchQueueRequestMutation(data => {
    if (data) {
      dispatch({
        type: 'SET_REQUESTS',
        payload: requests.map(req => (req.id === data.id ? data : req)),
      });
      dispatch({ type: 'SET_SELECTED_ITEM', payload: data });
    }
  });

  const handleReopen = useCallback(() => {
    if (!request.canReopen) return;
    setIsReopenOpen(true);
  }, [request.canReopen]);

  const handleReopenSubmit = useCallback(
    ({ comment }: { comment: string }) => {
      reopenMutation.mutate({ id: request.id, status: 'Open', comment });
      setIsReopenOpen(false);
    },
    [reopenMutation, request.id]
  );

  // Title from title field, identifier, or short ID
  const shortId = getShortId(request.id);
  const title = request.title || request.identifier || `Request #${shortId}`;

  // Clear pending message when request changes
  useEffect(() => {
    setPendingMessage(null);
  }, [request.id]);

  // Handler for when composer starts sending
  const handleMessageSending = useCallback((content: string) => {
    setPendingMessage({
      content,
      timestamp: new Date(),
    });
  }, []);

  // Handler for when message is successfully sent - clear pending state
  const handleMessageSent = useCallback(() => {
    setPendingMessage(null);
  }, []);

  const handleTitleSaved = useCallback(
    (data: AskRequest) => {
      dispatch({
        type: 'SET_REQUESTS',
        payload: requests.map(req => (req.id === data.id ? data : req)),
      });
      dispatch({ type: 'SET_SELECTED_ITEM', payload: data });
    },
    [dispatch, requests]
  );

  return (
    <div className={classNames(prefix(), { [prefix('--blocker')]: isBlocker })}>
      {/* Header with gradient background */}
      <div
        className={classNames(prefix('__header-wrapper'), {
          [prefix('__header-wrapper--blocker')]: isBlocker && !isGroupContainer,
          [prefix('__header-wrapper--group')]: isGroupContainer,
        })}
      >
        <div className={prefix('__header')}>
          <IconButton icon={<IconChevron />} onClick={onBack} size="large" className={prefix('__back-btn')} />
          <div className={prefix('__title-container')}>
            <MobileEditableTitle
              requestId={request.id}
              title={title}
              className={prefix('__title')}
              onSaved={handleTitleSaved}
            />
            {(isGroupContainer || hasParentGroup || dueDate) && (
              <div className={prefix('__meta-row')}>
                {isGroupContainer && <RequestType type={request.type} size="default" showTooltip={false} />}
                {hasParentGroup && !isGroupContainer && (
                  <div
                    className={prefix('__group-member-icon')}
                    onClick={handleGoToParentGroup}
                    title={parentGroupName ? `In group: ${parentGroupName}` : 'In a group'}
                  >
                    <IconFolder />
                  </div>
                )}
                {dueDate && <DueDateBadge date={dueDate} status={isDueDatePastDue ? 'past-due' : 'pending'} />}
              </div>
            )}
          </div>
          <div className={prefix('__header-actions')}>
            {/* Assignment and blocker actions are not valid for group containers */}
            {!isGroupContainer && <AssignmentButton request={request} canManageQueue={canManageQueue} mobile />}
            {/* Blocker toggle - only for accountants */}
            {!isGroupContainer && canManageQueue && (
              <>
                {showBlockerToggle && (
                  <IconButton
                    icon={<IconPromote />}
                    onClick={handleTogglePriority}
                    size="large"
                    tooltip={isBlocker ? 'Remove blocker' : 'Mark as blocker'}
                    selected={isBlocker}
                    type="switch"
                    disabled={priorityMutation.isPending}
                    className="blocker-toggle"
                  />
                )}
                {showReopen && (
                  <IconButton
                    icon={<IconReopen />}
                    onClick={handleReopen}
                    size="large"
                    tooltip="Reopen request"
                    disabled={reopenMutation.isPending}
                    className="reopen-toggle"
                  />
                )}
              </>
            )}
            {/* Group containers: promote Dissolve + AI Summary directly in header */}
            {isGroupContainer ? (
              <>
                <IconButton
                  icon={<IconKaiSynth />}
                  onClick={() => setIsSummarizeOpen(true)}
                  size="large"
                  tooltip="AI Summary"
                  className={prefix('__ai-btn')}
                />
                <IconButton
                  icon={<IconFolderDelete />}
                  onClick={handleDissolveGroup}
                  size="large"
                  tooltip="Dissolve Group"
                  className={prefix('__dissolve-btn')}
                />
              </>
            ) : (
              <IconButton
                icon={<IconMoreV />}
                onClick={() => setIsActionsOpen(true)}
                size="large"
                className={prefix('__more-btn')}
              />
            )}
          </div>
        </div>
      </div>

      {/* Message List */}
      <div className={prefix('__messages')}>
        {movedOutOfView ? (
          <EmptyState
            icon={movedIcon}
            title="This request moved"
            description={`It now lives in ${targetLabel}.`}
            action={{
              label: 'Back to list',
              onClick: onBack,
              variant: 'primary',
              icon: <IconChevron />,
            }}
            secondaryAction={
              targetView
                ? {
                    label: `View in ${targetLabel}`,
                    onClick: handleGoToTargetView,
                    icon: movedIcon,
                  }
                : undefined
            }
          />
        ) : (
          <MessageList
            ref={messageListRef}
            request={request}
            comments={comments}
            mobile
            fixedHeight
            pendingMessage={pendingMessage}
          />
        )}
      </div>

      {/* Composer */}
      {!movedOutOfView && (
        <div className={prefix('__composer-container')}>
          <div className={prefix('__composer')}>
            {cannotReopen ? (
              <div className={prefix('__cannot-reopen')}>
                <IconQuestion />
                <span>This request cannot be reopened.</span>
              </div>
            ) : (
              <CMComposer
                ref={composerRef}
                request={request}
                mobile
                context={CMComposerContexts.queue}
                onMessageSending={handleMessageSending}
                onMessageSent={handleMessageSent}
              />
            )}
          </div>
        </div>
      )}

      {/* Actions Panel (slide-up) */}
      <MobileActionsPanel
        request={request}
        isOpen={isActionsOpen}
        onClose={() => setIsActionsOpen(false)}
        canManageQueue={canManageQueue}
        allowResolveDialog
        groupCount={groups.length}
        hasParentGroup={hasParentGroup}
        hasOtherGroups={hasOtherGroups}
        isGroupContainer={isGroupContainer}
        onAddToGroup={() => {
          setIsGroupPickerMove(false);
          setIsGroupPickerOpen(true);
        }}
        onRemoveFromGroup={handleRemoveFromGroup}
        onMoveToGroup={() => {
          setIsGroupPickerMove(true);
          setIsGroupPickerOpen(true);
        }}
        onDissolveGroup={handleDissolveGroup}
        onCreateGroup={handleCreateGroupWithAskRequests}
      />

      {/* Group Picker Panel */}
      <MobileGroupPickerPanel
        isOpen={isGroupPickerOpen}
        onClose={() => setIsGroupPickerOpen(false)}
        groups={groups}
        activeGroupId={request.parentId ?? null}
        onGroupSelect={handleGroupPickerSelect}
        mode="assign"
        excludeGroupId={isGroupPickerMove ? request.parentId ?? undefined : undefined}
      />

      <ReopenDialog
        isOpen={isReopenOpen}
        isPending={reopenMutation.isPending}
        onClose={() => setIsReopenOpen(false)}
        onSubmit={handleReopenSubmit}
      />

      {/* AI Summary panel - accessible directly from group header */}
      <MobileSlideUpPanel
        isOpen={isSummarizeOpen}
        onClose={() => setIsSummarizeOpen(false)}
        title="AI Summary"
        titleIcon={<IconKaiSynth />}
        showBetaBadge
        fullSizeOnOpen
      >
        <SummarizePanel request={request} className="mobile-summarize-panel" />
      </MobileSlideUpPanel>
    </div>
  );
};

export default MobileDetailsScreen;
